/**
 * MFA Service
 * API Layer - Handles all MFA-related API calls
 * Location: components/MFA/MFAService.ts
 */

import { BASE_URL } from "../../App_Settings/app_settings";

/**
 * Get authentication token from localStorage
 */
const getAuthToken = (): string => {
  try {
    const tokenData = localStorage.getItem("token");
    if (tokenData) {
      const parsed = JSON.parse(tokenData);
      return parsed.token || "";
    }
    return "";
  } catch (error) {
    console.error("Error getting auth token:", error);
    return "";
  }
};

/**
 * Send MFA OTP code to user
 * @param userId - The user ID
 * @param method - Delivery method: 'email', 'sms', or 'both'
 */
export const sendMFACodeAPI = async (userId: number, method: string): Promise<boolean> => {
  try {
    const token = getAuthToken();
    const response = await fetch(`${BASE_URL}/Users/resendotp`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      //   body: JSON.stringify({ userId, method })
    });

    if (!response.ok) {
      throw new Error(`Failed to send MFA code: ${response.statusText}`);
    }

    return true;
  } catch (error) {
    console.error("Error sending MFA code:", error);
    throw error;
  }
};

/**
 * Verify MFA OTP code
 * @param otp - The 6-digit OTP code
 */
export const verifyMFACodeAPI = async (
  otp: string
): Promise<{ success: boolean; message?: string }> => {
  try {
    const token = getAuthToken();
    const response = await fetch(`${BASE_URL}/Users/verifyotp`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ otp }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        message: errorData.message || "Failed to verify OTP",
      };
    }

    const data = await response.json().catch(() => ({}));
    return {
      success: true,
      message: data.message || "OTP verified successfully",
    };
  } catch (error) {
    console.error("Error verifying OTP:", error);
    return {
      success: false,
      message: error instanceof Error ? error.message : "Network error occurred",
    };
  }
};

/**
 * Resend MFA OTP code
 */
export const resendMFACodeAPI = async (userId: number, method: string): Promise<boolean> => {
  return sendMFACodeAPI(userId, method);
};
